import React, { useRef } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useIdleTimeout } from './useIdleTimeout';

const MINUTE_MS = 60 * 1000;
const WARN_LEAD_MS = 5 * MINUTE_MS;
const DEFAULT_TIMEOUT_MINUTES = 60;

// REACT_APP_IDLE_TIMEOUT_MINUTES exists for the local 2-minute demo only
// (see scripts/start-local.sh). A console-settable build-time env var must
// not be able to silently rewrite the ASVS 7.3 idle control upward (or down
// to an immediate sign-out), so anything outside the integer range 1..60 —
// unset, NaN, non-integer, zero, negative, or over 60 — falls back to the
// production default of 60 rather than being honoured verbatim.
function clampIdleTimeoutMinutes(raw) {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > DEFAULT_TIMEOUT_MINUTES) return DEFAULT_TIMEOUT_MINUTES;
  return n;
}

// "4:59" — minutes unpadded, seconds zero-padded. Rounded up so the banner
// never shows 0:00 while there is still time on the clock.
function formatCountdown(ms) {
  const totalSeconds = Math.ceil(Math.max(0, ms) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Idle sign-out for a signed-in session (CONF-239, ASVS L2 D4: 60 minutes
 * without activity). Mounted by App.js's AuthWrapper only while a user is set.
 *
 * The idle hook's state lives here, in a leaf that renders nothing until the
 * warning, so its once-a-second countdown re-renders only this component and
 * never the configurator above it. Plain Tailwind and no toast: the toast
 * containers live below AuthWrapper.
 *
 * On a tab return the hook calls `onRevalidate` for both `visibilitychange`
 * and `focus`; an in-flight flag keeps that to one Cognito round trip. Only a
 * session that comes back without tokens counts as gone — a thrown error or
 * no answer at all is "unknown", and a network blip never signs anyone out.
 *
 * @param {object} props
 * @param {() => void} props.onSignOut - called once, at the timeout.
 * @param {() => void} props.onSessionGone - called when Cognito reports no tokens.
 * @param {number} [props.timeoutMinutes] - defaults to REACT_APP_IDLE_TIMEOUT_MINUTES
 *   clamped to the integer range 1..60, else 60.
 */
export function IdleSessionGuard({
  onSignOut,
  onSessionGone,
  timeoutMinutes = clampIdleTimeoutMinutes(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES),
}) {
  const timeoutMs = timeoutMinutes * MINUTE_MS;
  const warnMs = Math.max(timeoutMs - WARN_LEAD_MS, timeoutMs / 2);

  const revalidatingRef = useRef(false);

  const revalidate = async () => {
    if (revalidatingRef.current) return;
    revalidatingRef.current = true;
    try {
      const session = await fetchAuthSession();
      if (session && !session.tokens) onSessionGone();
    } catch {
      // Unknown, not gone: leave the session alone.
    } finally {
      revalidatingRef.current = false;
    }
  };

  // The hook keeps its callbacks in a latest-value ref, so these need no
  // memoizing.
  const { extend, warned, remainingMs } = useIdleTimeout({
    timeoutMs,
    warnMs,
    onTimeout: onSignOut,
    onRevalidate: revalidate,
  });

  if (!warned) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[1000] flex max-w-sm items-center gap-4 rounded-lg border border-orange-500 bg-black px-4 py-3 text-sm text-white shadow-2xl"
    >
      <p className="m-0">{`You'll be signed out in ${formatCountdown(remainingMs)} for inactivity.`}</p>
      <button
        type="button"
        onClick={extend}
        className="shrink-0 rounded bg-orange-500 px-3 py-1.5 font-semibold text-black hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-300"
      >
        Stay signed in
      </button>
    </div>
  );
}

export default IdleSessionGuard;
